import type { PluginBlock, PluginPageSetup, PluginPageView } from "./view-renderer"

type FieldLike = {
  key: string
  type?: string
  sensitive?: boolean
}

const sensitiveKeyPattern = /(api[_-]?key|access[_-]?token|refresh[_-]?token|connection[_-]?token|password|secret|credential)/i
const redacted = "[redacted]"

function asRecord(value: unknown) {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

function formFields(input?: { fields?: FieldLike[]; sections?: Array<{ fields?: FieldLike[] }> }) {
  return [...(input?.fields ?? []), ...(input?.sections ?? []).flatMap((section) => section.fields ?? [])]
}

function sensitiveKeys(input: FieldLike[]) {
  return new Set(
    input
      .filter((field) => field.type === "password" || field.sensitive || sensitiveKeyPattern.test(field.key))
      .map((field) => field.key),
  )
}

function redactValues(values: Record<string, unknown> | undefined, fields: FieldLike[] = [], sensitiveValues = new Set<string>()) {
  if (!values) return values
  const keys = sensitiveKeys(fields)
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      keys.has(key) || sensitiveKeyPattern.test(key) || (typeof value === "string" && sensitiveValues.has(value))
        ? redacted
        : value,
    ]),
  )
}

function addSensitiveValues(input: Set<string>, values: Record<string, unknown> | undefined, fields: FieldLike[] = []) {
  if (!values) return input
  const keys = sensitiveKeys(fields)
  Object.entries(values)
    .filter(([key, value]) => typeof value === "string" && (keys.has(key) || sensitiveKeyPattern.test(key)))
    .forEach((entry) => input.add(entry[1] as string))
  return input
}

function addSensitiveValuesFromBlock(input: Set<string>, block: PluginBlock): Set<string> {
  if (block.type === "form") return addSensitiveValues(input, block.values, formFields(block))
  if (block.type !== "table") return input
  return Object.values(block.expandedRows ?? {})
    .flat()
    .reduce(addSensitiveValuesFromBlock, input)
}

function sensitiveValues(view: PluginPageView) {
  return view.blocks.reduce(
    addSensitiveValuesFromBlock,
    addSensitiveValues(new Set<string>(), view.setup?.form?.values, formFields(view.setup?.form)),
  )
}

function redactDeep(value: unknown, sensitive: Set<string>, key = ""): unknown {
  if (sensitiveKeyPattern.test(key)) return redacted
  if (typeof value === "string") return sensitive.has(value) ? redacted : value
  if (Array.isArray(value)) return value.map((item) => redactDeep(item, sensitive))
  if (typeof value !== "object" || value === null) return value
  return Object.fromEntries(Object.entries(value).map(([entryKey, entryValue]) => [entryKey, redactDeep(entryValue, sensitive, entryKey)]))
}

function redactSetup(setup: PluginPageSetup | undefined, sensitive: Set<string>): PluginPageSetup | undefined {
  if (!setup?.form) return setup
  return {
    ...setup,
    form: {
      ...setup.form,
      values: redactValues(setup.form.values, formFields(setup.form), sensitive),
    },
  }
}

function redactBlock(block: PluginBlock, sensitive: Set<string>): PluginBlock {
  if (block.type === "form") {
    return {
      ...block,
      values: redactValues(block.values, formFields(block), sensitive),
    }
  }
  if (block.type === "registry") {
    return {
      ...block,
      props: redactDeep(block.props, sensitive) as Record<string, unknown> | undefined,
      slots: redactDeep(block.slots, sensitive) as typeof block.slots,
    }
  }
  if (block.type !== "table") return block
  return {
    ...block,
    expandedRows: Object.fromEntries(
      Object.entries(block.expandedRows ?? {}).map(([id, blocks]) => [id, blocks.map((item) => redactBlock(item, sensitive))]),
    ),
  }
}

export function redactPluginPageView(view: PluginPageView): PluginPageView {
  const sensitive = sensitiveValues(view)
  return {
    ...view,
    setup: redactSetup(view.setup, sensitive),
    blocks: view.blocks.map((block) => redactBlock(block, sensitive)),
  }
}

export function redactedRecord(value: unknown, fields: FieldLike[] = []) {
  return redactValues(asRecord(value), fields)
}
